import { Injectable } from '@angular/core';
import { ChapterType } from 'libraries/service/resources/quotation-quotations/IQuotationsQuotation';
import { ConfigurationResourceService } from 'libraries/service/resources/configuration/configurationResource.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { IItemQuote } from 'libraries/interfaces/ISearchResponse';
import { EVatDisplayMode } from 'libraries/service/vat/vat.interface';
import { IQuoteCreationConfigurationYii } from "libraries/interfaces/watabi/iquote-creation-configuration";

export interface ITypeBlocksAndSectionsInChapterConfig {
    title: string;
    defaultChapterCode: string;
    repartition: IRepartitionInChapterConfig;
    exclude: IExcludeTypesInChapterConfig;
}

export interface IExcludeTypesInChapterConfig {
    sectionType: string[];
    blockType: string[];
    blockCategory: string[];
}

export interface IRepartitionInChapterConfig {

    fees: {
        sectionType: string[];
        blockType: string[];
    };
    services: {
        sectionType: string[];
        blockType: string[];
    };
    trip: {
        sectionType: string[];
        blockType: string[];
    };
}

@Injectable()
export class QuoteConfigService {
    public readonly defaultQuoteCreationConfigurationYiiConfig: IQuoteCreationConfigurationYii = {
        writeMode: {
            simpleEdit: true,
            priceGrid: {
                selected: false,
                index: 0,
            },
        },
        priceConfiguration: {
            total: true,
            travellerTotal: true,
            options: true,
            fees: false,
            insurances: true,
            pricing: true,
        },
        quantityParameters: {
            ADT: {
                value: 2,
                min: 1,
                max: 9
            },
            CHD: {
                value: 0,
                min: 0,
                max: 9
            },
            INF: {
                value: 0,
                min: 0,
                max: 9
            }
        },
        vat: {
            vatRate: 0.2,
            vatDisplayMode: EVatDisplayMode.includingVAT
        },
    };

    public readonly defaultTypeBlocksAndSectionsInChapterConfig: ITypeBlocksAndSectionsInChapterConfig[] = [
        {
            "title": "Vol + Terrestre + H\u00e9bergement",
            "defaultChapterCode": "fees",
            "repartition": {
                "fees": {
                    "sectionType": [
                        "notIncludedPrice"
                    ],
                    "blockType": [
                        "options"
                    ]
                },
                "services": {
                    "sectionType": [
                        "totalPrice"
                    ],
                    "blockType": [
                        "travellerTotal",
                        "total"
                    ]
                },
                "trip": {
                    "sectionType": [],
                    "blockType": []
                }
            },
            "exclude": {
                "sectionType": [],
                "blockType": [],
                "blockCategory": [
                    "activity",
                    "goodtoknow",
                    "miscellaneous",
                    "car",
                    "place",
                    "meal",
                    "strongpoint"
                ]
            }
        },
        {
            "title": "Vol + Terrestre",
            "defaultChapterCode": "fees",
            "repartition": {
                "fees": {
                    "sectionType": [
                        "notIncludedPrice"
                    ],
                    "blockType": [
                        "options"
                    ]
                },
                "services": {
                    "sectionType": [
                        "totalPrice"
                    ],
                    "blockType": [
                        "travellerTotal",
                        "total"
                    ]
                },
                "trip": {
                    "sectionType": [],
                    "blockType": []
                }
            },
            "exclude": {
                "sectionType": [],
                "blockType": [],
                "blockCategory": [
                    "accomodation",
                    "activity",
                    "goodtoknow",
                    "miscellaneous",
                    "car",
                    "place",
                    "meal",
                    "strongpoint"
                ]
            }
        },
        {
            "title": "Grille compl\u00e8te",
            "defaultChapterCode": "fees",
            "repartition": {
                "fees": {
                    "sectionType": [
                        "notIncludedPrice"
                    ],
                    "blockType": [
                        "options"
                    ]
                },
                "services": {
                    "sectionType": [
                        "totalPrice"
                    ],
                    "blockType": [
                        "travellerTotal"
                    ]
                },
                "trip": {
                    "sectionType": [],
                    "blockType": []
                }
            },
            "exclude": {
                "sectionType": [],
                "blockType": [
                    "total"
                ],
                "blockCategory": [
                    "miscellaneous",
                    "place",
                    "strongPoint"
                ]
            }
        }
    ];

    constructor(
        private readonly configurationResourceService: ConfigurationResourceService,
    ) { }

    public getChapterCodeInConfig(sectionType: string, config: ITypeBlocksAndSectionsInChapterConfig): string {
        let result: string = config.defaultChapterCode;

        if (config.repartition.fees.sectionType.find((element: string) => {
            return element.toLowerCase() === sectionType.toLowerCase();
        })) {
            result = ChapterType.fees;
        }

        if (config.repartition.services.sectionType.find((element: string) => {
            return element.toLowerCase() === sectionType.toLowerCase();
        })) {
            result = ChapterType.services;
        }

        if (config.repartition.trip.sectionType.find((element: string) => {
            return element.toLowerCase() === sectionType.toLowerCase();
        })) {
            result = ChapterType.trip;
        }

        return result;
    }

    public getQuoteTypeBlocksAndSectionsInChapterConfig(quoteItem: IItemQuote): Observable<ITypeBlocksAndSectionsInChapterConfig> {
        const index: number = quoteItem.price &&
            quoteItem.price.retail &&
            quoteItem.price.retail.writeModeSelection ?
            quoteItem.price.retail.writeModeSelection.priceGrid.index :
            null;
        return index !== null && index !== undefined ?
            this.configurationResourceService
                .get(
                    ['wui_watabi', 'protected', 'config', 'init-price-grid', 'init-price-grid.json'],
                    this.defaultTypeBlocksAndSectionsInChapterConfig,
                )
                .pipe(
                    map((typeBlocksAndSectionsInChapterConfigs: ITypeBlocksAndSectionsInChapterConfig[]) => typeBlocksAndSectionsInChapterConfigs[index])
                )
            :
            of(null);
    }
}
